<template>
    <PageContainer>

        <Breadcrumbs :breadcrumbs="breadcrumbs" />

        <h1 class="heading mb-5">
            Oh Hello!
        </h1>

        <div class="flex flex-wrap -mx-5 mb-5">

            <div class="w-full lg:w-1/2 px-5">

                <div class="mb-5">
                    <h3 class="mb-3">
                        From the littlest of questions to planning your dream installation, we would love to hear from you!
                    </h3>
                    <p class="mb-3">
                        If you have placed an online order you may be able to find your answer in our
                        <nuxt-link to="/faq">
                            FAQ page
                        </nuxt-link>
                        or you can WhatsApp us at <a :href="customerServicesWhatsAppUrl">{{ customerServicesWhatsAppDisplay }}</a>.
                    </p>
                    <p class="mb-8">
                        <a :href="customerServicesWhatsAppUrl">
                            <img
                                src="https://content.bubblegumballoons.com/assets/9b147576-6ed6-4d75-8081-0dfb78005e88"
                                alt="Customer Services WhatsApp"
                            >
                        </a>
                    </p>
                    <p class="mb-3">
                        If you would like to discuss Balloons for an Event, Branded Balloons or Corporate Balloons
                        please email <a href="mailto:sales@bubblegumballoons.co.uk">sales@bubblegumballoons.co.uk</a>
                        or WhatsApp us at <a :href="salesWhatsAppUrl">{{ salesWhatsAppDisplay }}</a>.
                    </p>
                    <p class="mb-8">
                        <a :href="salesWhatsAppUrl">
                            <img
                                src="https://content.bubblegumballoons.com/assets/7b4cb937-b840-4ad5-bbc9-5d376ab9a15b"
                                alt="Sales WhatsApp"
                            >
                        </a>
                    </p>
                </div>

                <div class="mb-8">
                    <h4 class="mb-1">
                        General Enquiries
                    </h4>
                    <div class="text-sm">
                        <div>
                            <a
                                href="tel:01252626697"
                                target="_blank"
                            >01252 626697</a> (Mon - Fri, 9-5pm)
                        </div>
                        <div>
                            <a
                                href="mailto:customerservices@bubblegumballoons.co.uk"
                                target="_blank"
                            >customerservices@bubblegumballoons.co.uk</a>
                        </div>
                    </div>
                </div>

                <div class="mb-8">
                    <h4 class="mb-1">
                        Events Enquiries (Including Corporate & Wedding)
                    </h4>
                    <div class="text-sm">
                        <div>Contact Laura and Nish:</div>
                        <div>
                            <a
                                href="mailto:sales@bubblegumballoons.co.uk"
                                target="_blank"
                            >sales@bubblegumballoons.co.uk</a>
                        </div>
                    </div>
                </div>

                <div class="mb-8">
                    <h4 class="mb-1">
                        Marketing & PR
                    </h4>
                    <div class="text-sm">
                        <div>Contact Emily:</div>
                        <div>
                            <a
                                href="mailto:marketing@bubblegumballoons.co.uk"
                                target="_blank"
                            >marketing@bubblegumballoons.co.uk</a>
                        </div>
                    </div>
                </div>

                <div class="mb-3">
                    <h4 class="mb-1">
                        Accounts
                    </h4>
                    <div class="text-sm">
                        <div>
                            <a
                                href="mailto:accounts@bubblegumballoons.co.uk"
                                target="_blank"
                            >accounts@bubblegumballoons.co.uk</a>
                        </div>
                    </div>
                </div>

            </div>

            <div class="w-full lg:w-1/2 px-5 mt-5 lg:mt-0">

                <TextInput
                    v-model="name"
                    :invalid="$v.name.$error"
                    class="mb-5"
                    label="Name"
                    focused
                >
                    <template #errors>
                        <div v-if="!$v.name.required && $v.name.$dirty">
                            Please enter your name.
                        </div>
                    </template>
                </TextInput>

                <TextInput
                    v-model="email"
                    :invalid="$v.email.$error"
                    class="mb-5"
                    label="Email"
                >
                    <template #errors>
                        <div v-if="!$v.email.required && $v.email.$dirty">
                            Please enter your email address.
                        </div>
                        <div v-if="!$v.email.email && $v.email.$dirty">
                            Please enter a valid email address.
                        </div>
                    </template>
                </TextInput>

                <TextArea2
                    v-model="comment"
                    :invalid="$v.comment.$error"
                    :success="!!successMessage"
                    class="mb-5"
                    label="Comment"
                >
                    <template #errors>
                        <div v-if="!$v.comment.required && $v.comment.$dirty">
                            Please enter a comment.
                        </div>
                    </template>
                    <template #success>
                        {{ successMessage }}
                    </template>
                </TextArea2>

                <vue-recaptcha
                    class="mb-5"
                    sitekey="6LcIS7QUAAAAAPK7SG7RFwJQM2__50ie4a6OPwSI"
                    @verify="allowSubmission"
                />

                <Button
                    :disabled="isSubmitDisabled"
                    text="Submit"
                    @click="submit"
                />

            </div>

        </div>

    </PageContainer>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators';
import VueRecaptcha from 'vue-recaptcha';
import Breadcrumbs from '~/components/Breadcrumbs';
import TextInput from '~/components/Input';
import TextArea2 from '~/components/TextArea';
import Button from '~/components/Button';
import handleException from '../plugins/error-handler.client';
import PageContainer from '@/components/PageContainer.vue';

export default {

    components: {
        PageContainer,
        Breadcrumbs,
        TextInput,
        TextArea2,
        VueRecaptcha,
        Button,
    },

    data() {
        return {
            name: '',
            email: '',
            comment: '',
            isSubmitDisabled: true,
            successMessage: '',
            breadcrumbs: [
                {
                    name: 'Home',
                    link: '/',
                },
                {
                    name: 'Contact Us',
                    link: '',
                },
            ],
        };
    },

    computed: {
        customerServicesWhatsApp() {
            return this.$store.getters['settings/getSetting']('whatsapp_customer_services') || '447484956612';
        },
        salesWhatsApp() {
            return this.$store.getters['settings/getSetting']('whatsapp_sales') || '447716637712';
        },
        customerServicesWhatsAppUrl() {
            return `https://wa.me/${this.customerServicesWhatsApp}`;
        },
        salesWhatsAppUrl() {
            return `https://wa.me/${this.salesWhatsApp}`;
        },
        customerServicesWhatsAppDisplay() {
            const num = this.customerServicesWhatsApp;
            return `+${num.slice(0, 2)} ${num.slice(2, 6)} ${num.slice(6)}`;
        },
        salesWhatsAppDisplay() {
            const num = this.salesWhatsApp;
            return `+${num.slice(0, 2)} ${num.slice(2, 6)} ${num.slice(6)}`;
        },
    },

    methods: {
        allowSubmission() {
            this.isSubmitDisabled = false;
        },
        submit() {
            this.$v.$touch();
            if (this.$v.$invalid) return;

            this.$axios.$post('/contact-us', {
                name: this.name,
                email: this.email,
                comment: this.comment,
                userAgent: navigator.userAgent,
                orderId: this.$store.getters['basket/id'],
            })
                .then(() => {
                    this.successMessage = 'Message sent successfully.';
                    this.name = '';
                    this.email = '';
                    this.comment = '';
                    this.$v.$reset();
                })
                .catch((error) => {
                    handleException(error);
                    this.$sentry.captureException(error);
                });
        },
    },

    head() {
        return {
            title: 'Contact Us',
            script: [
                { src: 'https://www.google.com/recaptcha/api.js?onload=vueRecaptchaApiLoaded&render=explicit' },
            ],
        };
    },

    validations: {
        name: {
            required,
        },
        email: {
            required,
            email,
        },
        comment: {
            required,
        },
    },

};
</script>

<style lang="scss" scoped>
.heading {
    @apply font-heading text-primary;
    font-size: 300%;
}
a {
    @apply text-primary transition-fast no-underline;
    &:hover {
        @apply opacity-75;
    }
}
</style>
